import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CapDossierCover, CapDossierFinding } from '@dosfilos/domain';

// ─── Otorgar con condiciones ─────────────────────────────────────────────────
//
// Nació de un caso real: un programa que no cumplía el estándar Y cuyo centro
// NO PODÍA arreglarlo dentro de ese programa —las áreas faltantes se enseñan en
// otro peldaño de la escalera—. Devolverlo era un bucle; rechazarlo,
// desproporcionado; aprobarlo dejó una certificación limpia sobre tres
// hallazgos bloqueantes, y lo único que quedó escrito fue la prosa del revisor.
//
// Lo que este diálogo impide es que la cuarta salida se vuelva una aprobación
// con adorno: las condiciones se MARCAN sobre los hallazgos que el expediente ya
// calculó —no se redactan de cero— y la fecha es obligatoria, porque sin fecha
// no es una condición sino un deseo.

export interface ConditionDraft {
  code: CapDossierFinding['code'];
  area?: string;
  note?: string;
}

export function ConditionalApprovalDialog({
  open,
  cover,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  cover: CapDossierCover;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (conditions: { items: ConditionDraft[]; reviewBy: string }) => void;
}): JSX.Element {
  const { t } = useTranslation('cap');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [reviewBy, setReviewBy] = useState('');

  /** Un hallazgo puede repetirse por área (dos áreas cortas), así que la
   *  identidad de una condición es el par código+área, no el código. */
  const keyOf = (f: CapDossierFinding): string => `${f.code}:${f.area ?? ''}`;

  const items = useMemo(
    () =>
      cover.findings
        .filter((f) => checked.has(keyOf(f)))
        .map((f) => ({
          code: f.code,
          ...(f.area ? { area: f.area } : {}),
          ...(notes[keyOf(f)]?.trim() ? { note: notes[keyOf(f)]!.trim() } : {}),
        })),
    [cover.findings, checked, notes],
  );

  // Una fecha pasada la rechaza el servidor; el campo no la ofrece siquiera.
  const today = new Date().toISOString().slice(0, 10);
  const canConfirm = items.length > 0 && reviewBy > today && !busy;

  const toggle = (key: string): void =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{t('conditions.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">{t('conditions.body')}</Typography>

          {cover.findings.length === 0 ? (
            // Sin hallazgos no hay nada que condicionar: aprobar limpio es la
            // decisión correcta, y decirlo evita inventar una condición.
            <Alert severity="info">{t('conditions.nothingToCondition')}</Alert>
          ) : (
            <Stack spacing={0.5}>
              {cover.findings.map((f) => {
                const key = keyOf(f);
                const area = f.area ? t(`subjectAreas.${f.area}`) : '';
                return (
                  <Stack key={key} spacing={0.5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked.has(key)}
                          onChange={() => toggle(key)}
                          disabled={busy}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t(`dossier.findings.${f.code}`, { ...f.params, area })}
                        </Typography>
                      }
                    />
                    {checked.has(key) && (
                      <TextField
                        size="small"
                        fullWidth
                        value={notes[key] ?? ''}
                        onChange={(e) => setNotes((n) => ({ ...n, [key]: e.target.value }))}
                        label={t('conditions.noteLabel')}
                        placeholder={t('conditions.notePlaceholder')}
                        disabled={busy}
                        sx={{ ml: 4, width: 'calc(100% - 32px)', mb: 1 }}
                      />
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}

          <TextField
            type="date"
            label={t('conditions.reviewBy')}
            value={reviewBy}
            onChange={(e) => setReviewBy(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: today } }}
            helperText={t('conditions.reviewByHelp')}
            disabled={busy}
          />

          <Alert severity="warning">{t('conditions.warning')}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy} sx={{ textTransform: 'none' }}>
          {t('conditions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!canConfirm}
          onClick={() =>
            onConfirm({ items, reviewBy: new Date(`${reviewBy}T12:00:00`).toISOString() })
          }
          sx={{ textTransform: 'none' }}
        >
          {t('conditions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
